<template>
  <div class="card residence-bar-card">
    <div class="card-header">
      <h3 class="card-title">{{ title }}</h3>
    </div>
    <div class="card-body">
      <div v-if="areas.length > 0" class="chart-area" :style="{ height: chartHeight + 'px' }">
        <canvas ref="chartCanvas"></canvas>
      </div>
      <div v-else class="empty-note">{{ emptyLabel }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip } from 'chart.js';
import { colors } from '../../../../consts/colors';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  // [{ name, count, percentage }]
  areas: {
    type: Array,
    default: () => []
  },
  emptyLabel: {
    type: String,
    default: '-'
  }
});

const chartCanvas = ref(null);
let chartInstance = null;

const chartHeight = computed(() => Math.max(120, props.areas.length * 34 + 40));

const initializeChart = () => {
  if (!chartCanvas.value) return;

  const ctx = chartCanvas.value.getContext('2d');
  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: props.areas.map(area => area.name),
      datasets: [{
        data: props.areas.map(area => area.percentage),
        backgroundColor: colors.blue[500],
        borderRadius: 2,
        barThickness: 18,
      }]
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => context.parsed.x.toFixed(1) + '%'
          }
        }
      },
      scales: {
        x: {
          min: 0,
          max: 100,
          ticks: {
            stepSize: 25,
            color: colors.gray[500],
          },
          grid: {
            color: colors.gray[200],
          }
        },
        y: {
          ticks: {
            color: colors.textPrimary,
            autoSkip: false,
          },
          grid: {
            display: false
          }
        }
      }
    }
  });
};

const updateChart = () => {
  if (!chartInstance) {
    nextTick(() => initializeChart());
    return;
  }

  chartInstance.data.labels = props.areas.map(area => area.name);
  chartInstance.data.datasets[0].data = props.areas.map(area => area.percentage);
  chartInstance.resize();
  chartInstance.update();
};

onMounted(() => {
  nextTick(() => initializeChart());
});

watch(() => props.areas, () => {
  updateChart();
}, { deep: true });
</script>

<style lang="scss" scoped>
.residence-bar-card {
  background: v-bind('colors.white');
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
  border: none;

  .card-header {
    background-color: v-bind('colors.white');
    border: none;
    padding: 0;
    margin-bottom: 16px;
  }

  .card-title {
    font-size: 18px;
    font-weight: 600;
    color: v-bind('colors.textPrimary');
    margin: 0;
  }

  .card-body {
    padding: 0;
  }

  .chart-area {
    position: relative;
  }

  .empty-note {
    text-align: center;
    color: v-bind('colors.gray[500]');
    padding: 24px 0;
  }
}
</style>
